import { Alert, LinearProgress } from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Fragment, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import z from 'zod';
import {
    getActivity,
    getActivityChangeRequest,
    getActivityTypes,
    getActivityValidationRules,
    getParticipantRoles,
} from '../../api/activities-nim';
import { ActivityTypeCode, DocumentType } from '../../api/dto';
import { getActivityTestQuestions, getDurationTypes, getFacilities, getWorkingTimes } from '../../api/values';
import { ImpactHelmet } from '../../components/ImpactHelmet';
import { defaultUrlParamsSchema, withUrlParams } from '../../components/wrappers/withUrlParams';
import { ActivityForm } from '../../forms/documents/ActivityForm';
import { ActivityPageVariant } from './ActivityPageVariant';

const schema = z.union([
    defaultUrlParamsSchema.extend({
        variant: z.union([z.literal(ActivityPageVariant.CREATE), z.literal(ActivityPageVariant.CREATE_TEMPLATE)]),
    }),
    defaultUrlParamsSchema.extend({
        variant: z.union([
            z.literal(ActivityPageVariant.CLONE),
            z.literal(ActivityPageVariant.READ),
            z.literal(ActivityPageVariant.CLONE_FROM_TEMPLATE),
        ]),
        pathParams: z.object({
            id: z.coerce.number(),
        }),
    }),
    defaultUrlParamsSchema.extend({
        variant: z.literal(ActivityPageVariant.READ_CHANGE_REQUEST),
        pathParams: z.object({
            id: z.coerce.number(),
            changeRequestId: z.coerce.number(),
        }),
    }),
]);

const ActivityPageContent: React.FC<z.infer<typeof schema>> = ({ variant, pathParams }) => {
    const intl = useIntl();
    const location = useLocation();
    const state = location.state as { activityTypeCode?: ActivityTypeCode } | undefined;
    const isUrgentIntervention = state?.activityTypeCode === ActivityTypeCode.URGENT_INTERVENTION;
    const { id } = pathParams as { id: number };

    const formKey = variant === ActivityPageVariant.CREATE ? location.key : 'default';

    const {
        data: activityData,
        isLoading: isActivityDataLoading,
        isError: activityDataFailed,
        remove,
        refetch,
    } = useQuery(
        [
            DocumentType.ACTIVITY,
            [ActivityPageVariant.CREATE, ActivityPageVariant.CREATE_TEMPLATE].includes(variant) ? undefined : id,
            variant === ActivityPageVariant.READ_CHANGE_REQUEST ? pathParams.changeRequestId : undefined,
        ],
        async () => {
            if ([ActivityPageVariant.CREATE, ActivityPageVariant.CREATE_TEMPLATE].includes(variant)) {
                throw new Error();
            }
            const activity = await getActivity({ id: String(id) });
            const changeRequest =
                variant === ActivityPageVariant.READ_CHANGE_REQUEST
                    ? await getActivityChangeRequest({ id, changeRequestId: pathParams.changeRequestId })
                    : null;
            return { activity, changeRequest };
        },
        {
            enabled: variant !== ActivityPageVariant.CREATE && variant !== ActivityPageVariant.CREATE_TEMPLATE,
            refetchOnWindowFocus: true, // For the linked documents
        }
    );

    const refreshData = useCallback(() => {
        remove();
        return refetch();
    }, [remove, refetch]);

    const {
        data: rulesData,
        isLoading: isRulesDataLoading,
        isError: rulesDataFailed,
    } = useQuery(
        [
            DocumentType.ACTIVITY,
            variant === ActivityPageVariant.READ
                ? pathParams.id
                : variant === ActivityPageVariant.READ_CHANGE_REQUEST
                ? pathParams.changeRequestId
                : undefined,
            'rules',
        ] as const,
        () =>
            getActivityValidationRules({
                id:
                    variant === ActivityPageVariant.READ
                        ? pathParams.id
                        : variant === ActivityPageVariant.READ_CHANGE_REQUEST
                        ? pathParams.changeRequestId
                        : undefined,
            })
    );

    const values = useQueries({
        queries: [
            {
                queryKey: ['activityTypes'],
                queryFn: () => getActivityTypes(),
            },
            {
                queryKey: ['facilities'],
                queryFn: () => getFacilities(),
            },
            {
                queryKey: ['testQuestionTypes'],
                queryFn: () => getActivityTestQuestions(),
            },
            {
                queryKey: ['workingTimes'],
                queryFn: () => getWorkingTimes(),
            },
            {
                queryKey: ['durationTypes'],
                queryFn: () => getDurationTypes(),
            },
            {
                queryKey: ['participantRoles'],
                queryFn: () => getParticipantRoles({ activityType: null }),
            },
        ],
    });
    const [activityTypes, facilities, testQuestionTypes, workingTimes, durationTypes, participantRoles] = values.map(
        ({ data }) => data
    );
    const isValuesLoading = values.some(({ isLoading }) => isLoading);
    const isValuesFailed = values.some(({ isError }) => isError);
    if (
        (isActivityDataLoading &&
            variant !== ActivityPageVariant.CREATE &&
            variant !== ActivityPageVariant.CREATE_TEMPLATE) ||
        isRulesDataLoading ||
        isValuesLoading
    ) {
        return <LinearProgress />;
    } else if (activityDataFailed || rulesDataFailed || isValuesFailed) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <FormattedMessage id="activity.error" />
                <ImpactHelmet title={intl.formatMessage({ id: 'title.error' })} />
            </Alert>
        );
    } else if (
        (variant === ActivityPageVariant.CREATE ||
            variant === ActivityPageVariant.CREATE_TEMPLATE ||
            ([
                ActivityPageVariant.READ,
                ActivityPageVariant.CLONE,
                ActivityPageVariant.CLONE_FROM_TEMPLATE,
                ActivityPageVariant.READ_CHANGE_REQUEST,
            ].includes(variant) &&
                activityData)) &&
        !!activityTypes &&
        !!facilities &&
        !!testQuestionTypes &&
        !!workingTimes &&
        !!durationTypes &&
        !!participantRoles
    ) {
        return (
            <ActivityForm
                key={formKey}
                data={
                    variant === ActivityPageVariant.CREATE || variant === ActivityPageVariant.CREATE_TEMPLATE
                        ? { variant }
                        : variant === ActivityPageVariant.READ_CHANGE_REQUEST
                        ? {
                              variant,
                              activityData: activityData!.changeRequest!,
                              parentActivityData: activityData!.activity!,
                          }
                        : {
                              variant,
                              activityData: activityData!.activity!,
                          }
                }
                refreshData={refreshData}
                rules={rulesData}
                activityTypes={activityTypes}
                facilities={facilities}
                testQuestionTypes={testQuestionTypes}
                workingTimes={workingTimes}
                durationTypes={durationTypes}
                participantRoles={participantRoles}
                isUrgentIntervention={isUrgentIntervention}
            />
        );
    } else {
        return <Fragment />;
    }
};

export const ActivityPage = withUrlParams(ActivityPageContent, schema);
